import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import ButtonOutlined from "../../../../components/FormElements/ButtonOutlined";
import CustomSelect from "../../../../components/FormElements/Select";
import PageHeader from "../../../../components/PageHeader";
import branchApi from "../../../../store/branchStore/branchApi";
import { store as notificationStore } from "react-notifications-component";
import { EXPIRATION_OPTIONS, CUSTOM_EXPIRATION_VALUE, DURATION_UNITS } from "../../../../config/expirationOptions";
import { BONUS_SOURCES, isSourceAvailable } from "../../../../config/bonusSources";
import styles from './bonusMode.module.scss';

export default function BonusMode() {
    const { t } = useTranslation();
    const info = useSelector((s) => s.branchReducer.info);

    const [saving, setSaving] = useState(false);
    const [sourceExpirations, setSourceExpirations] = useState({});
    const [setAllValue, setSetAllValue] = useState(null);
    const [customInputs, setCustomInputs] = useState({});
    const [setAllCustom, setSetAllCustom] = useState({ amount: '', unit: DURATION_UNITS[0] });

    const config = info?.configuration;
    const isCascade = config?.private_cashback?.is_cascade || false;

    useEffect(() => {
        if (info && isCascade) {
            loadBalanceExpiration();
        }
    }, [info]);

    const parseCustomValue = (ms) => {
        if (!ms || ms <= 0) return null;
        if (EXPIRATION_OPTIONS.some(o => o.value === ms)) return null;
        for (let i = DURATION_UNITS.length - 1; i >= 0; i--) {
            const unit = DURATION_UNITS[i];
            const amount = ms / unit.value;
            if (amount >= 1 && Number.isInteger(amount)) {
                return { amount: String(amount), unit };
            }
        }
        return { amount: String(Math.round(ms / 60000)), unit: DURATION_UNITS[0] };
    };

    const buildCustomInputsFromData = (data) => {
        const customs = {};
        Object.entries(data).forEach(([key, val]) => {
            const parsed = parseCustomValue(val);
            if (parsed) customs[key] = parsed;
        });
        return customs;
    };

    const loadBalanceExpiration = async () => {
        try {
            const res = await branchApi.getBalanceExpiration(info.id);
            if (res?.data) {
                setSourceExpirations(res.data);
                setCustomInputs(buildCustomInputsFromData(res.data));
            }
        } catch (e) {
            const fallback = config?.private_cashback?.expire_interval || 0;
            const defaults = {};
            BONUS_SOURCES.forEach(s => { defaults[s.key] = fallback; });
            setSourceExpirations(defaults);
        }
    };

    const handleApplyToAll = () => {
        if (setAllValue == null) return;
        if (setAllValue.value === CUSTOM_EXPIRATION_VALUE) {
            const amount = parseFloat(setAllCustom.amount);
            if (!amount || amount <= 0) return;
            const ms = amount * setAllCustom.unit.value;
            const updated = {};
            const newCustom = {};
            BONUS_SOURCES.forEach(s => {
                updated[s.key] = ms;
                newCustom[s.key] = { amount: setAllCustom.amount, unit: setAllCustom.unit };
            });
            setSourceExpirations(updated);
            setCustomInputs(newCustom);
        } else {
            const updated = {};
            BONUS_SOURCES.forEach(s => { updated[s.key] = setAllValue.value; });
            setSourceExpirations(updated);
            setCustomInputs({});
        }
    };

    const isCustomValue = (val) => {
        return val > 0 && !EXPIRATION_OPTIONS.some(o => o.value === val);
    };

    const handleSourceExpChange = (sourceKey, option) => {
        if (option.value === CUSTOM_EXPIRATION_VALUE) {
            setCustomInputs(prev => ({
                ...prev,
                [sourceKey]: prev[sourceKey] || { amount: '', unit: DURATION_UNITS[0] },
            }));
        } else {
            setSourceExpirations(prev => ({ ...prev, [sourceKey]: option.value }));
            setCustomInputs(prev => {
                const next = { ...prev };
                delete next[sourceKey];
                return next;
            });
        }
    };

    const handleCustomAmountChange = (sourceKey, amount) => {
        setCustomInputs(prev => ({ ...prev, [sourceKey]: { ...prev[sourceKey], amount } }));
        const num = parseFloat(amount);
        if (num > 0) {
            const ms = num * (customInputs[sourceKey]?.unit?.value || DURATION_UNITS[0].value);
            setSourceExpirations(prev => ({ ...prev, [sourceKey]: ms }));
        }
    };

    const handleCustomUnitChange = (sourceKey, unitOption) => {
        setCustomInputs(prev => ({ ...prev, [sourceKey]: { ...prev[sourceKey], unit: unitOption } }));
        const num = parseFloat(customInputs[sourceKey]?.amount);
        if (num > 0) {
            const ms = num * unitOption.value;
            setSourceExpirations(prev => ({ ...prev, [sourceKey]: ms }));
        }
    };

    const getAvailableSources = () => {
        return BONUS_SOURCES.filter(s => isSourceAvailable(s, config));
    };

    const allSourcesConfigured = () => {
        const available = getAvailableSources();
        return available.every(s => sourceExpirations[s.key] !== undefined);
    };

    const handleSave = async () => {
        if (!allSourcesConfigured()) return;

        setSaving(true);
        try {
            await branchApi.updateBalanceExpiration(info.id, sourceExpirations);

            notificationStore.addNotification({
                message: t('save') + ' - OK',
                type: 'success',
                insert: 'top',
                container: 'top-right',
                dismiss: { duration: 3000 },
            });
        } catch (e) {
            console.error('Failed to save bonus expirations:', e);
        } finally {
            setSaving(false);
        }
    };

    const translatedOptions = EXPIRATION_OPTIONS.map(o => ({
        label: t(o.label),
        value: o.value,
    }));

    const translatedUnits = DURATION_UNITS.map(u => ({
        label: t(u.label),
        value: u.value,
    }));

    if (!isCascade) {
        return (
            <>
                <PageHeader title={t('NSBonusMode.title')} />
                <div className={styles.container}>
                    <div className={styles.currentMode}>
                        {t('NSBonusMode.currentMode')}: <span>{t('NSBonusMode.accumulative')}</span>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader title={t('NSBonusMode.title')} />
            <div className={styles.container}>
                <div className={styles.currentMode}>
                    {t('NSBonusMode.currentMode')}: {' '}
                    <span>{t('NSBonusMode.cascade')}</span>
                </div>

                <div className={styles.sectionTitle}>
                    {t('NSBonusMode.perSourceConfig')}
                </div>

                <div className={styles.setAllRow}>
                    <div className={styles.setAllSelect}>
                        <CustomSelect
                            options={translatedOptions}
                            value={setAllValue || { label: t('NSBonusMode.setAll'), value: -1 }}
                            onSelect={(o) => setSetAllValue(o)}
                        />
                    </div>
                    {setAllValue?.value === CUSTOM_EXPIRATION_VALUE && (
                        <div className={styles.customInputRow} onClick={(e) => e.stopPropagation()}>
                            <input
                                type="number"
                                min="1"
                                className={styles.customAmountInput}
                                value={setAllCustom.amount}
                                onChange={(e) => setSetAllCustom(prev => ({ ...prev, amount: e.target.value }))}
                                onClick={(e) => e.stopPropagation()}
                                placeholder={t('NSBonusMode.amount')}
                            />
                            <div className={styles.customUnitSelect}>
                                <CustomSelect
                                    options={translatedUnits}
                                    value={{ label: t(setAllCustom.unit.label), value: setAllCustom.unit.value }}
                                    onSelect={(o) => setSetAllCustom(prev => ({ ...prev, unit: DURATION_UNITS.find(u => u.value === o.value) || DURATION_UNITS[0] }))}
                                />
                            </div>
                        </div>
                    )}
                    <div className={styles.applyBtnWrap}>
                        <ButtonOutlined onClick={handleApplyToAll}>
                            {t('NSBonusMode.applyToAll')}
                        </ButtonOutlined>
                    </div>
                </div>

                <table className={styles.sourceTable}>
                    <thead>
                        <tr>
                            <th>{t('NSBonusMode.source')}</th>
                            <th>{t('NSBonusMode.expiration')}</th>
                            <th>{t('NSBonusMode.status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {BONUS_SOURCES.map(source => {
                            const available = isSourceAvailable(source, config);
                            const currentVal = sourceExpirations[source.key] || 0;
                            const hasCustom = customInputs[source.key] || isCustomValue(currentVal);
                            const selectedOption = hasCustom
                                ? translatedOptions.find(o => o.value === CUSTOM_EXPIRATION_VALUE)
                                : (translatedOptions.find(o => o.value === currentVal) || translatedOptions[0]);

                            return (
                                <tr key={source.key} className={!available ? styles.sourceDisabled : ''}>
                                    <td>{t(source.labelKey)}</td>
                                    <td>
                                        {available ? (
                                            <div className={styles.selectCell}>
                                                <CustomSelect
                                                    options={translatedOptions}
                                                    value={selectedOption}
                                                    onSelect={(o) => handleSourceExpChange(source.key, o)}
                                                />
                                                {hasCustom && (
                                                    <div className={styles.customInputRow} onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className={styles.customAmountInput}
                                                            value={customInputs[source.key]?.amount || ''}
                                                            onChange={(e) => handleCustomAmountChange(source.key, e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            placeholder={t('NSBonusMode.amount')}
                                                        />
                                                        <div className={styles.customUnitSelect}>
                                                            <CustomSelect
                                                                options={translatedUnits}
                                                                value={translatedUnits.find(u => u.value === (customInputs[source.key]?.unit?.value || DURATION_UNITS[0].value)) || translatedUnits[0]}
                                                                onSelect={(o) => handleCustomUnitChange(source.key, DURATION_UNITS.find(u => u.value === o.value) || DURATION_UNITS[0])}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span>—</span>
                                        )}
                                    </td>
                                    <td>
                                        {available ? (
                                            <span className={styles.statusEnabled}>{t('NSBonusMode.enabled')}</span>
                                        ) : (
                                            <span className={styles.statusDisabled}>{t('NSBonusMode.featureDisabled')}</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className={styles.actions}>
                    <ButtonFilled
                        loading={saving}
                        disabled={!allSourcesConfigured()}
                        onClick={handleSave}
                    >
                        {t('save')}
                    </ButtonFilled>
                </div>
            </div>
        </>
    );
}
